/**
 * Notification Navigation Utilities
 *
 * Handles routing logic for notification clicks with support for:
 * - Same-page refresh via query parameter (React Router same-path behavior)
 * - Cross-page navigation with push/replace history
 */

/**
 * Generate a unique refresh parameter to force navigation on the same route
 *
 * React Router ignores history.push() to an identical pathname, so we append
 * a timestamp query parameter to make the route "different" and trigger re-renders.
 *
 * @returns {string} Refresh parameter: "refresh=1713186441234"
 */
export const createRefreshParam = () => `refresh=${Date.now()}`;

/**
 * Navigate to a target path, intelligently choosing push vs replace
 *
 * - If already on the target path: use history.replace() + refresh param (same-page refresh)
 * - If on a different path: use history.push() + refresh param (navigate + prepare for re-render)
 *
 * This solves React Router's behavior where history.push() to an identical pathname is ignored.
 *
 * @param {Object} params
 * @param {Object} params.history - React Router history object (from withRouter HOC or useHistory hook)
 * @param {string} params.targetPath - Pathname to navigate to (e.g., "/app/cm/alerts/ABC123")
 * @param {string} params.search - Query string to append (e.g., "?tab=downloadHistory")
 * @returns {void}
 *
 * @example
 * // From /app/cm/alerts/ABC123 → /app/cm/alerts/ABC123 (refresh same page)
 * navigateWithRefresh({
 *   history,
 *   targetPath: '/app/cm/alerts/ABC123',
 *   search: '?tab=downloadHistory'
 * });
 * // Uses replace: pathname stays same, query param changes → triggers re-render
 *
 * @example
 * // From /home → /app/cm/alerts/ABC123 (navigate to new page)
 * navigateWithRefresh({
 *   history,
 *   targetPath: '/app/cm/alerts/ABC123',
 *   search: '?tab=downloadHistory'
 * });
 * // Uses push: changes pathname → browser history + re-render
 */
export const navigateWithRefresh = ({ history, targetPath, search }) => {
  const currentPath = window.location.pathname;
  const refreshParam = createRefreshParam();
  const fullSearch = `${search}&${refreshParam}`;

  if (currentPath === targetPath) {
    // Already on target path — use replace to avoid duplicate history entries
    // The refresh param ensures the component sees a "new" query and re-renders
    history.replace({
      pathname: targetPath,
      search: fullSearch
    });
  } else {
    // Different path — use push to add to browser history
    history.push({
      pathname: targetPath,
      search: fullSearch
    });
  }
};

/**
 * Parse additional properties from a notification object
 *
 * Handles two API response formats:
 * 1. Old: additionalProperties is a JSON string
 * 2. New: additionalProperties is already an object
 *
 * @param {Object} notification - Notification object from API
 * @returns {Object} Parsed additional properties (empty object if missing)
 *
 * @example
 * const props = parseNotificationAdditionalProps(notification);
 * console.log(props.module, props.alertId, props.caseId);
 */
export const parseNotificationAdditionalProps = (notification) => {
  if (!notification?.additionalProperties) {
    return {};
  }

  if (typeof notification.additionalProperties === 'string') {
    try {
      return JSON.parse(notification.additionalProperties);
    } catch {
      return {};
    }
  }

  return notification.additionalProperties || {};
};
